import { Controller } from '../utils/controller';
import debounce from 'lodash/debounce';
import { isMobile } from '../utils/ismobile';

interface ItemType {
  itemLabel: string;
  menuItemIndex: number;
  hasSafeTargetArea: number;
  hasSubmenu: boolean;
}

export default class extends Controller {
  static targets = [
    'menu',
    'menuList',
    'menuContainer',
    'menuTitle',
    'backButton',
    'searchBar',
    'menuItem',
    'loyaltyButton',
    'stickyHeader',
    'menuMask',
    'hamburgerIcon',
    'closeIcon',
    'nestedMenuContainer',
    'nestedMenu',
    'nestedSubmenu',
    'safeTriangle'
  ];
  static requiredTargets = [];

  static values = {
    isBgTransparent: Boolean,
    isOpen: {
      type: Boolean,
      default: false
    },
    hasHideMenuMaskEvent: {
      type: Boolean,
      default: false
    }
  };

  static classes = [
    'hidden',
    'hiddenSticky',
    'menuOpened',
    'overflowHidden',
    'transparentEnabled',
    'transparentDisabled',
    'animation',
    'slideEnd',
    'slideEnter',
    'slideLeave'
  ];
  static requiredClasses = [];

  // TARGETS
  declare readonly hasStickyHeaderTarget: boolean;
  declare readonly hasMenuTitleTarget: boolean;
  declare readonly hasSearchBarTarget: boolean;
  declare readonly hasBackButtonTarget: boolean;
  declare readonly hasMenuListTarget: boolean;
  declare readonly hasMenuContainerTarget: boolean;
  declare readonly hasLoyaltyButtonTarget: boolean;
  declare readonly hasMenuMaskTarget: boolean;
  declare readonly hasTransparentDisabledClass: boolean;
  declare readonly hasTransparentEnabledClass: boolean;
  declare readonly hasNestedMenuContainerTarget: boolean;
  declare stickyHeaderTarget?: HTMLElement;
  declare menuTitleTarget?: HTMLElement;
  declare searchBarTarget?: HTMLElement;
  declare backButtonTarget?: HTMLElement;
  declare menuTarget?: HTMLElement;
  declare menuListTarget?: HTMLElement;
  declare menuContainerTarget?: HTMLElement;
  declare loyaltyButtonTarget?: HTMLElement;
  declare hamburgerIconTarget: HTMLElement;
  declare closeIconTarget: HTMLElement;
  declare menuMaskTarget?: HTMLElement;
  declare menuItemTargets?: HTMLElement[];
  declare safeTargetArea?: HTMLElement;
  declare safeTargetAreaTimer?: NodeJS.Timeout;
  declare sourceTarget?: HTMLElement;

  // VALUES
  declare isBgTransparentValue: boolean;
  declare isOpenValue: boolean;
  declare hasHideMenuMaskEventValue: boolean;
  declare isSafeTargetAreaEnabled: boolean;

  // CLASSES
  declare readonly hasAnimationClass: boolean;
  declare readonly hasSlideEnterClass: boolean;
  declare readonly hasSlideLeaveClass: boolean;
  declare animationClasses?: string[];
  declare slideEndClasses?: string[];
  declare slideEnterClasses?: string[];
  declare slideLeaveClasses?: string[];
  declare menuOpenedClasses?: string[];
  declare hiddenStickyClasses?: string[];
  declare transparentDisabledClass?: string;
  declare transparentEnabledClass?: string;
  declare hiddenClasses?: string[];
  declare basisClass: string;
  declare overflowHiddenClass: string;

  // LOCAL STATE
  private lastScroll: number;
  private headerHeight: number;
  private debouncedResetHeaderSize;

  initialize(): void {
    this.headerHeight = 0;
    this.hideUtilities();
  }

  connect() {
    this.lastScroll = 0;
    this.basisClass = 'basis-1/2';

    if (this.isBgTransparentValue) {
      this.debouncedResetHeaderSize = debounce(this.resetHeaderSize, 150);
      this.resetHeaderSize();
      window.addEventListener('resize', this.debouncedResetHeaderSize);
    }

    this.hasHideMenuMaskEventValue &&
      this.element.addEventListener('menuMaskHidden', this.handleHideMenuMaskEvent);
  }

  disconnect(): void {
    window.removeEventListener('resize', this.debouncedResetHeaderSize);
    this.hasHideMenuMaskEventValue &&
      this.element.removeEventListener('menuMaskHidden', this.handleHideMenuMaskEvent);
    document.body.removeEventListener('mousemove', this.mouseMove);
  }

  handleHideMenuMaskEvent = (event: CustomEvent) => {
    if (event?.detail?.params?.hasSubmenu) {
      this.showMenuMask(event?.detail);
    } else {
      this.hideMenuMask();
    }
  };

  private readonly resetHeaderSize = () => {
    this.headerHeight = (this.element as HTMLDivElement).offsetHeight;
  };

  openMenu() {
    this.menuOpenedClasses &&
      !this.stickyHeaderTarget?.classList.contains(this.menuOpenedClasses.join(' ')) &&
      this.stickyHeaderTarget?.classList.add(...this.menuOpenedClasses);

    this.hasMenuContainerTarget &&
      this.menuContainerTarget != null &&
      this.show(this.menuContainerTarget);

    if (this.hasNestedMenuContainerTarget) {
      document.body.classList.add(this.overflowHiddenClass);
      this.nestedMenuContainerTarget.classList.toggle(this.slideEnterClass);
    }
  }

  closeMenu() {
    this.menuOpenedClasses &&
      this.stickyHeaderTarget?.classList.contains(this.menuOpenedClasses.join(' ')) &&
      this.stickyHeaderTarget?.classList.remove(...this.menuOpenedClasses);

    this.hasMenuContainerTarget &&
      this.menuContainerTarget != null &&
      this.hide(this.menuContainerTarget);

    this.closeSubMenu();

    if (this.hasNestedMenuContainerTarget) {
      document.body.classList.remove(this.overflowHiddenClass);
      this.nestedMenuContainerTarget.classList.toggle(this.slideEnterClass);
    }
  }

  toggleMenu() {
    if (this.isOpenValue) {
      this.closeMenu();
      this.hide(this.closeIconTarget);
      this.show(this.hamburgerIconTarget);

      if (this.hasNestedMenuTarget) {
        this.nestedMenuTargets.forEach((el) => {
          el.classList.add(this.slideLeaveClasses);
        });
      }
      if (this.hasNestedSubmenuTarget) {
        this.nestedSubmenuTargets.forEach((el) => {
          el.classList.add(this.slideLeaveClasses);
        });
      }
    } else {
      this.openMenu();
      this.show(this.closeIconTarget);
      this.hide(this.hamburgerIconTarget);
      this.customAnimation(this.menuListTarget, this.slideEnterClasses, this.slideEndClasses);
    }
    this.isOpenValue = !this.isOpenValue;
  }

  itemSelected(e: { params: ItemType } & MouseEvent) {
    const {
      params: { itemLabel, menuItemIndex, hasSubmenu }
    } = e;

    if (!hasSubmenu || !isMobile()) {
      return false;
    }

    e.preventDefault();

    if (this.hasMenuTitleTarget && this.menuTitleTarget) {
      this.menuTitleTarget.innerHTML = itemLabel;
    }

    this.showUtilities();
    this.customAnimation(this.menuListTarget, this.slideLeaveClasses, this.slideEndClasses);
    this.openSubMenu(menuItemIndex);
  }

  showUtilities() {
    this.hasSearchBarTarget && this.searchBarTarget && this.hide(this.searchBarTarget);
    this.hasMenuTitleTarget && this.menuTitleTarget && this.show(this.menuTitleTarget);
    this.hasBackButtonTarget && this.backButtonTarget && this.show(this.backButtonTarget);
  }

  closeSubMenu() {
    this.hideUtilities();
    this.customAnimation(this.menuListTarget, this.slideEnterClasses, this.slideEndClasses);

    this.menuItemTargets?.forEach((item) => {
      const parentElement = item.parentElement;
      if (parentElement?.children != null) {
        const subMenu = parentElement.children.namedItem('subMenu');

        parentElement.classList.add(this.basisClass);
        this.show(parentElement);
        this.show(item);
        try {
          JSON.parse(item.dataset.stackedNavHasSubmenuParam || 'null') &&
            subMenu &&
            this.hide(subMenu);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Could not parse', item.dataset.stackedNavHasSubmenuParam, 'reason', e);
        }
      }
    });

    this.menuItemTargets?.forEach(({ parentElement }) => {
      parentElement && this.show(parentElement);
    });

    this.hasLoyaltyButtonTarget && this.loyaltyButtonTarget && this.show(this.loyaltyButtonTarget);
  }

  hideUtilities() {
    this.hasSearchBarTarget && this.searchBarTarget && this.show(this.searchBarTarget);
    this.hasMenuTitleTarget && this.menuTitleTarget && this.hide(this.menuTitleTarget);
    this.hasBackButtonTarget && this.backButtonTarget && this.hide(this.backButtonTarget);
  }

  openSubMenu(index: number) {
    this.menuItemTargets?.forEach((item, i) => {
      const parentElement = item.parentElement;

      if (parentElement) {
        this.hide(parentElement);
        if (this.hasLoyaltyButtonTarget && this.loyaltyButtonTarget) {
          this.hide(this.loyaltyButtonTarget);
        }

        if (i === index) {
          const subMenu = parentElement.children.namedItem('subMenu');
          parentElement.classList.remove(this.basisClass);

          this.show(parentElement);
          this.hide(item);
          if (subMenu) {
            this.show(subMenu);
          }
        }
      }
    });
  }

  updateSafeTargetArea = (e: MouseEvent) => {
    e.preventDefault();
    const target = e.target as HTMLElement;
    const sourceRectangle = (this.sourceTarget as HTMLElement)?.getBoundingClientRect();
    const { clientX, clientY } = e;
    if (
      !this.safeTargetAreaTimer &&
      (clientX < sourceRectangle.left ||
        clientX > sourceRectangle.right ||
        clientY < sourceRectangle.top ||
        clientY > sourceRectangle.bottom)
    ) {
      this.safeTargetAreaTimer = setTimeout(() => {
        this.hide(this.safeTargetArea);
        this.safeTargetAreaTimer = undefined;
      }, 1000);
    }
    if (
      clientX > sourceRectangle.left &&
      clientX < sourceRectangle.right &&
      clientY > sourceRectangle.top &&
      clientY < sourceRectangle.bottom
    ) {
      const sourceRectangle = (target as HTMLElement)?.getBoundingClientRect();
      this.safeTargetArea &&
        (this.safeTargetArea.style.clipPath = `polygon(${
          ((sourceRectangle.x + e.offsetX) / window.innerWidth) * 100
        }% 0, 100% 100%, 0 100%)`);
    }
  };
  trackSafeTargetArea(e: { params: ItemType } & MouseEvent) {
    const {
      currentTarget,
      params: { hasSafeTargetArea }
    } = e;
    if (!this.isSafeTargetAreaEnabled && hasSafeTargetArea) {
      this.safeTargetAreaTimer = undefined;
      this.isSafeTargetAreaEnabled = true;
      this.sourceTarget = currentTarget as HTMLElement;
      this.safeTargetArea = (this.sourceTarget as HTMLElement).querySelector(
        'span.safeTargetArea'
      ) as HTMLElement;
      this.show(this.safeTargetArea);
      document.body.addEventListener('mousemove', this.updateSafeTargetArea);
    }
  }

  untrackSafeTargetArea(e: { params: ItemType } & MouseEvent) {
    const {
      params: { hasSafeTargetArea }
    } = e;
    if (this.isSafeTargetAreaEnabled && hasSafeTargetArea) {
      this.isSafeTargetAreaEnabled = false;
      this.sourceTarget = undefined;
      document.body.removeEventListener('mousemove', this.updateSafeTargetArea);
      this.safeTargetAreaTimer && clearTimeout(this.safeTargetAreaTimer);
      this.safeTargetAreaTimer = undefined;
    }
  }

  showMenuMask({ params }: { params: ItemType }) {
    const { hasSubmenu } = params;

    if (isMobile() || !hasSubmenu) {
      return false;
    }

    this.hasMenuMaskTarget && this.menuMaskTarget && this.show(this.menuMaskTarget);
  }

  hideMenuMask() {
    if (isMobile()) {
      return false;
    }

    this.hasMenuMaskTarget && this.menuMaskTarget && this.hide(this.menuMaskTarget);
  }

  hide(element: HTMLElement | Element | null | undefined) {
    this.hiddenClasses && element?.classList.add(...this.hiddenClasses);
  }

  show(element: HTMLElement | Element) {
    this.hiddenClasses && element.classList.remove(...this.hiddenClasses);
  }

  handleStickyOnScroll() {
    const currentScroll = window.scrollY;

    if (currentScroll >= this.lastScroll && currentScroll > (this.headerHeight || 0)) {
      this.hasStickyHeaderTarget &&
        this.hiddenStickyClasses &&
        this.stickyHeaderTarget?.classList.add(...this.hiddenStickyClasses);
    } else {
      this.hasStickyHeaderTarget &&
        this.hiddenStickyClasses &&
        this.stickyHeaderTarget?.classList.remove(...this.hiddenStickyClasses);
    }
    this.lastScroll = currentScroll;
  }

  handleFixedOnScroll() {
    const currentScroll = window.scrollY;

    if (currentScroll >= this.lastScroll && currentScroll > 0) {
      this.hasStickyHeaderTarget &&
        this.hiddenStickyClasses &&
        this.stickyHeaderTarget?.classList.add(...this.hiddenStickyClasses);
    } else {
      if (currentScroll <= this.headerHeight) {
        this.hasStickyHeaderTarget &&
          this.hasTransparentDisabledClass &&
          this.transparentDisabledClass &&
          this.stickyHeaderTarget?.classList.remove(this.transparentDisabledClass);

        this.hasStickyHeaderTarget &&
          this.hasTransparentDisabledClass &&
          this.transparentEnabledClass &&
          this.stickyHeaderTarget?.classList.add(this.transparentEnabledClass);

        return;
      }

      this.hasStickyHeaderTarget &&
        this.hiddenStickyClasses &&
        this.stickyHeaderTarget?.classList.remove(...this.hiddenStickyClasses);

      this.hasStickyHeaderTarget &&
        this.hasTransparentEnabledClass &&
        this.transparentEnabledClass &&
        this.stickyHeaderTarget?.classList.remove(this.transparentEnabledClass);

      this.hasStickyHeaderTarget &&
        this.hasTransparentDisabledClass &&
        this.transparentDisabledClass &&
        this.stickyHeaderTarget?.classList.add(this.transparentDisabledClass);
    }
    this.lastScroll = currentScroll;
  }

  customAnimation(
    target: HTMLElement | Element | null | undefined,
    startClass?: string[],
    endClass?: string[]
  ) {
    if (!target || !startClass || !endClass || !this.hasAnimationClass) {
      return;
    }

    const animationClasses = [...this.animationClasses, endClass];

    target.classList.remove(...animationClasses);
    target.classList.add(...startClass);
    setTimeout(() => {
      target.classList.remove(...startClass);
      target.classList.toggle(...animationClasses);
    }, 0);
  }

  toggleNestedMenu(event) {
    const index = event.currentTarget.dataset.index;
    this.nestedMenuTargets[index].classList.toggle(this.slideLeaveClass);
  }

  toggleNestedSubmenu(event) {
    const parent = event.currentTarget.dataset.parent;
    const category = event.currentTarget.dataset.category;
    this.nestedSubmenuTargets.forEach((el) => {
      const nest_parent = el.dataset.parent;
      const nest_category = el.dataset.category;
      if (parent === nest_parent && category === nest_category) {
        el.classList.toggle(this.slideLeaveClass);
      }
    });
  }
}
